/**
 * The buttons installed plugins contribute to a place in the app — the header, the Audit screen,
 * the Plugins menu. Each sends its prompt to the agent in the active repository, as a work order,
 * the same way Vephus's own buttons do.
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Puzzle } from 'lucide-react'
import { ApiError } from '../lib/api'
import { PLUGIN_ICONS, runPluginCommand, usePluginCommands } from '../lib/plugins'
import { useI18n } from '../lib/i18n'
import { useActiveRepository } from '../lib/activeRepository'
import type { PluginCommand, PluginItem } from '../lib/types'

export function PluginButtons({ placement, compact = false }: { placement: PluginCommand['placement']; compact?: boolean }) {
  const { t } = useI18n()
  const commands = usePluginCommands(placement)
  const { active } = useActiveRepository()
  const [sent, setSent] = useState<string | null>(null)

  const run = useMutation({
    mutationFn: ({ plugin, command }: { plugin: PluginItem; command: PluginCommand }) => runPluginCommand(plugin, command, active?.id ?? null),
    onSuccess: (_, { command }) => setSent(command.id),
    onError: (error) => setSent(error instanceof ApiError && error.code === 'remote_off' ? 'remote_off' : 'failed'),
  })

  if (commands.length === 0) return null

  return (
    <div className={compact ? 'plugin-buttons plugin-buttons--compact' : 'plugin-buttons'}>
      {commands.map(({ plugin, command }) => {
        const Icon = PLUGIN_ICONS[command.icon] ?? Puzzle
        return (
          <button
            key={`${plugin.id}:${command.id}`}
            type="button"
            className="btn btn--sm btn--ghost"
            title={`${plugin.name} — ${command.description || command.title}`}
            onClick={() => run.mutate({ plugin, command })}
            disabled={run.isPending}
          >
            <Icon size={13} /> {command.title}
            {sent === command.id && <span className="tiny muted"> · {t('plugins.sent')}</span>}
          </button>
        )
      })}
    </div>
  )
}
